import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  List, Plus, MapPin, Clock, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  bozza: 'bg-slate-400',
  programmato: 'bg-blue-500',
  in_corso: 'bg-green-500',
  concluso: 'bg-slate-300',
  annullato: 'bg-red-400'
};

const statusLabels = {
  bozza: 'Bozza',
  programmato: 'Programmato',
  in_corso: 'In Corso',
  concluso: 'Concluso',
  annullato: 'Annullato'
};

export default function CalendarioEventi() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventi, setEventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // calendar or list

  const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  const months = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  useEffect(() => {
    fetchEventi();
  }, [currentDate]);

  const fetchEventi = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const response = await fetch(`${API_URL}/api/eventi?anno=${year}&mese=${month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEventi(data || []);
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore nel caricamento eventi');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get the day of week for first day (0=Sun, 1=Mon, etc)
    let startDay = firstDay.getDay();
    // Adjust for Monday start
    startDay = startDay === 0 ? 6 : startDay - 1;
    
    const days = [];
    
    // Add empty cells for days before first of month
    for (let i = 0; i < startDay; i++) {
      days.push({ day: null, events: [] });
    }
    
    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayEvents = eventi.filter(e => e.data === dateStr);
      days.push({ day, date: dateStr, events: dayEvents });
    }
    
    return days;
  };

  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="space-y-6" data-testid="calendario-eventi">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarIcon className="h-6 w-6" />
            Calendario Eventi
          </h1>
          <p className="text-gray-500 mt-1">Visualizza e gestisci gli eventi del mese</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
          >
            <CalendarIcon className="h-4 w-4 mr-1" />
            Calendario
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4 mr-1" />
            Lista
          </Button>
          <Button onClick={() => navigate('/eventi/nuovo')}>
            <Plus className="h-4 w-4 mr-1" />
            Nuovo Evento
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-6">
            <Button variant="outline" size="icon" onClick={goToPrevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold">
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Oggi
              </Button>
            </div>
            <Button variant="outline" size="icon" onClick={goToNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {viewMode === 'calendar' ? (
            /* Calendar View */
            <div className="border rounded-lg overflow-hidden">
              {/* Days header */}
              <div className="grid grid-cols-7 bg-gray-50 border-b">
                {daysOfWeek.map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-gray-600">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {days.map((dayData, index) => (
                  <div
                    key={index}
                    className={`min-h-[100px] border-b border-r p-1 ${
                      !dayData.day ? 'bg-gray-50' : ''
                    } ${isToday(dayData.date) ? 'bg-blue-50' : ''}`}
                  >
                    {dayData.day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${
                          isToday(dayData.date) ? 'text-blue-600' : 'text-gray-700'
                        }`}>
                          {dayData.day}
                        </div>
                        <div className="space-y-1">
                          {dayData.events.slice(0, 3).map(evento => (
                            <button
                              key={evento.evento_id}
                              onClick={() => setSelectedEvent(evento)}
                              className={`w-full text-left px-1.5 py-0.5 rounded text-xs text-white truncate ${
                                statusColors[evento.stato] || 'bg-gray-400'
                              } hover:opacity-80 transition-opacity`}
                            >
                              {evento.titolo}
                            </button>
                          ))}
                          {dayData.events.length > 3 && (
                            <div className="text-xs text-gray-500 px-1">
                              +{dayData.events.length - 3} altri
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* List View */
            <div className="space-y-3">
              {eventi.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nessun evento in questo mese
                </div>
              ) : (
                eventi.sort((a, b) => a.data.localeCompare(b.data)).map(evento => (
                  <div
                    key={evento.evento_id}
                    className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedEvent(evento)}
                  >
                    <div className={`w-1 h-full min-h-[60px] rounded-full ${statusColors[evento.stato]}`} />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium">{evento.titolo}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="h-3.5 w-3.5" />
                              {new Date(evento.data).toLocaleDateString('it-IT', { 
                                weekday: 'short', 
                                day: 'numeric', 
                                month: 'short' 
                              })}
                            </span>
                            {evento.ora_inizio && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {evento.ora_inizio}
                              </span>
                            )}
                            {evento.luogo && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                {evento.luogo}
                              </span>
                            )}
                          </div>
                        </div>
                        <Badge className={statusColors[evento.stato]?.replace('bg-', 'bg-opacity-20 text-') || ''}>
                          {statusLabels[evento.stato]}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="text-gray-500">Legenda:</span>
        {Object.entries(statusLabels).map(([key, label]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${statusColors[key]}`} />
            <span className="text-gray-600">{label}</span>
          </div>
        ))}
      </div>

      {/* Event Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${statusColors[selectedEvent?.stato]}`} />
              {selectedEvent?.titolo}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Data</p>
                  <p className="font-medium">
                    {new Date(selectedEvent.data).toLocaleDateString('it-IT', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Orario</p>
                  <p className="font-medium">
                    {selectedEvent.ora_inizio || '--:--'} - {selectedEvent.ora_fine || '--:--'}
                  </p>
                </div>
              </div>
              
              {selectedEvent.luogo && (
                <div>
                  <p className="text-sm text-gray-500">Luogo</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    {selectedEvent.luogo}
                  </p>
                </div>
              )}
              
              {selectedEvent.descrizione && (
                <div>
                  <p className="text-sm text-gray-500">Descrizione</p>
                  <p className="text-gray-700">{selectedEvent.descrizione}</p>
                </div>
              )}
              
              <div>
                <p className="text-sm text-gray-500">Stato</p>
                <Badge className={`${statusColors[selectedEvent.stato]} text-white`}>
                  {statusLabels[selectedEvent.stato]}
                </Badge>
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setSelectedEvent(null);
                    navigate(`/eventi/${selectedEvent.evento_id}`);
                  }}
                >
                  Visualizza Dettagli
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => {
                    setSelectedEvent(null);
                    navigate(`/checkin?evento=${selectedEvent.evento_id}`);
                  }}
                >
                  <Users className="h-4 w-4 mr-1" />
                  Gestisci Presenze
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
